import { reactive } from 'vue';

/** @typedef {import('../../domain/entities/Audit.types').AuditVerdict} AuditVerdict */
/** @typedef {import('../../domain/constants/checkoutAuditStatuses').CheckoutAuditFailureReason} FailureReason */

/**
 * @typedef {Object} AuditRun
 * @property {boolean} isRunning
 * @property {number} attemptCount   - Every attempt, including ones that produced nothing.
 * @property {AuditVerdict|null} verdict
 * @property {string|null} auditedNotes - The exact text reviewed, so a later edit can be spotted.
 * @property {string|null} baselineNotes - The note as a surface found it on a verdict it did not
 *   produce itself. After a reload it is the only thing an edit can be measured against.
 * @property {number|null} baselineAuditRunCount - The stored tally that verdict carried, so a later
 *   run can be seen to have replaced it.
 * @property {string|null} sessionActivityAuditId - The stored result this run produced.
 * @property {number|null} auditRunCount - Runs on this note across every client, ahead of the copy on
 *   the activity, which a signature can beat to the snapshot.
 * @property {FailureReason|null} failureReason
 * @property {number|null} runToken - Identifies the attempt in flight; see startAuditRun.
 * @property {string|null} status - The note's status when the run started; see isLocalAuditRunCurrent.
 */

/**
 * Module scoped: two surfaces can show one note at once, and reopening the summary step must not
 * forget a review and spend another model call on the same answer.
 */
const runsBySessionActivityId = reactive(new Map());

/** @type {AuditRun} */
const NO_RUN = Object.freeze({
  isRunning: false,
  attemptCount: 0,
  verdict: null,
  auditedNotes: null,
  baselineNotes: null,
  baselineAuditRunCount: null,
  sessionActivityAuditId: null,
  auditRunCount: null,
  failureReason: null,
  runToken: null,
  status: null,
});

/** Unique per attempt across every note, so a forgotten run's token can never be handed out again. */
let lastRunToken = 0;

/** Bounded: a shift walks through far more notes than can be on a surface at once. */
const MAX_TRACKED_RUNS = 50;

function ensureRun(sessionActivityId) {
  if (!runsBySessionActivityId.has(sessionActivityId)) {
    if (runsBySessionActivityId.size >= MAX_TRACKED_RUNS) {
      // Map iterates in insertion order, so this is the oldest entry not in flight.
      const stale = [...runsBySessionActivityId].find(([, run]) => !run.isRunning);

      if (stale) {
        runsBySessionActivityId.delete(stale[0]);
      }
    }

    runsBySessionActivityId.set(sessionActivityId, { ...NO_RUN });
  }

  return runsBySessionActivityId.get(sessionActivityId);
}

/** @returns {AuditRun} */
function readRun(sessionActivityId) {
  return (sessionActivityId && runsBySessionActivityId.get(sessionActivityId)) || NO_RUN;
}

// eslint-disable-next-line import/prefer-default-export
export function useAuditRunState() {
  return {
    getAuditRun: readRun,

    isAuditRunning: (sessionActivityId) => readRun(sessionActivityId).isRunning,

    isAuditRunUnresolved: (sessionActivityId) => {
      const run = readRun(sessionActivityId);

      return run.isRunning || !!run.failureReason;
    },

    /** First reading wins: a later one would already include the edit it is meant to reveal. */
    rememberBaselineNotes: (sessionActivityId, notes, storedAuditRunCount = null) => {
      if (!sessionActivityId) {
        return;
      }

      const run = ensureRun(sessionActivityId);

      if (run.baselineNotes === null) {
        run.baselineNotes = notes ?? null;
        run.baselineAuditRunCount = storedAuditRunCount;
      }
    },

    /** Call where a result is dismissed, or this client keeps answering with its own copy. */
    forgetAuditRun: (sessionActivityId) => {
      runsBySessionActivityId.delete(sessionActivityId);
    },

    /**
     * The token identifies this attempt, and only the attempt still holding it may finish the run.
     *
     * @returns {{ attemptNumber: number, runToken: number|null }} The attempt number is for logging.
     */
    startAuditRun: (sessionActivityId, status = null) => {
      if (!sessionActivityId) {
        return { attemptNumber: 0, runToken: null };
      }

      lastRunToken += 1;

      const run = ensureRun(sessionActivityId);
      run.isRunning = true;
      run.attemptCount += 1;
      run.failureReason = null;
      run.runToken = lastRunToken;
      run.status = status;

      return { attemptNumber: run.attemptCount, runToken: lastRunToken };
    },

    /** A run that produced nothing leaves the verdict alone, so an earlier result stays valid. */
    finishAuditRun: (
      sessionActivityId,
      { verdict, auditedNotes, sessionActivityAuditId, auditRunCount, failureReason },
      runToken,
    ) => {
      const run = sessionActivityId ? runsBySessionActivityId.get(sessionActivityId) : null;

      if (!run || run.runToken !== runToken) {
        return;
      }

      run.isRunning = false;
      run.failureReason = failureReason;

      if (verdict) {
        run.verdict = verdict;
        run.auditedNotes = auditedNotes;
        run.sessionActivityAuditId = sessionActivityAuditId ?? null;
        run.auditRunCount = auditRunCount ?? null;
      }
    },
  };
}
